import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { sidebarEntries, sidebarSectionTitles } from "@models/SidebarEntries";
import type { SidebarEntry, SidebarSectionKey } from "@models/SidebarEntries";
import type { SystemIconType } from "@shared/designSystem/SystemIcon";
import type { ComboBoxOption } from "@shared/designSystem/ComboBox";
import { SharedApplication } from "@shared/core/SharedApplication";
import type { LocalStorageInterface } from "@shared/core/LocalStorageInterface";
import type { Organization } from "@models/Organization";
import type { User } from "firebase/auth";
import type { AuthenticatedDashboardUser } from "@services/UsersService";
import { canAccessPilotOnboarding } from "@screens/Onboarding/PilotOnboardingAccess";

export interface SidebarOption {
    key: string;
    label: string;
    icon: SystemIconType;
    isActive: boolean;
    isLocked: boolean;
    path?: string;
    externalLink?: string;
}

export interface SidebarSection {
    key: Exclude<SidebarSectionKey, 'bottom'>;
    title: string;
    options: SidebarOption[];
    isOpen: boolean;
    hiddenCount: number;
}

export interface SidebarUserCard {
    name: string;
    subtitle?: string;
    imageURL?: string;
}

const PILOT_ONBOARDING_KEY = "Pilot Onboarding";
const PILOT_ONBOARDING_PATH = "/onboarding/pilot";

const CLOSED_SECTIONS_STORAGE_KEY = "qontact.sidebar.closedSections";
const SECTION_KEYS: SidebarSectionKey[] = ['principal', 'comunicacion', 'gestion', 'bottom'];

// Stored as the CLOSED set so new/unknown sections default to open.
export const parseClosedSections = (raw: string | null | undefined): SidebarSectionKey[] => {
    if (!raw) {
        return [];
    }
    try {
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.filter((key): key is SidebarSectionKey => SECTION_KEYS.includes(key as SidebarSectionKey));
    } catch {
        return [];
    }
};

export const serializeClosedSections = (keys: SidebarSectionKey[]): string => JSON.stringify(keys);

interface BuildSidebarOptionsParams {
    entries: Record<string, SidebarEntry>;
    flags: Record<string, boolean | undefined>;
    pathname: string;
}

// Pure so tests can exercise visibility/locking without rendering the hook.
export const buildSidebarOptions = ({ entries, flags, pathname }: BuildSidebarOptionsParams): (SidebarOption & { section: SidebarSectionKey })[] => {
    const isLiteVersion = flags.LiteVersion === true;

    return Object.entries(entries).flatMap(([key, entry]) => {
        const hasRequiredFlags = entry.requiredFlag
            ? Array.isArray(entry.requiredFlag)
                ? entry.requiredFlag.some((flag) => flags[flag])
                : Boolean(flags[entry.requiredFlag])
            : true;

        // For Lite: show all, lock if not liteAllowed. For full version: only show if allowed by feature flag.
        if (!isLiteVersion && !hasRequiredFlags) {
            return [];
        }

        return [{
            key,
            label: key,
            icon: entry.icon,
            section: entry.section,
            path: entry.path,
            externalLink: entry.externalLink,
            isLocked: isLiteVersion && entry.liteAllowed !== true,
            isActive: entry.path ? pathname.startsWith(entry.path) : false,
        }];
    });
};

interface UseSidebarScreenViewModelParams {
    organization: Organization | undefined;
    organizations: Organization[] | undefined;
    user: User | null | undefined;
    dashboardUser: AuthenticatedDashboardUser | undefined;
    isSystemAdmin: boolean;
    onOrganizationSelected: (organization: Organization) => void;
    onLockedOptionSelected: () => void;
    localStorage?: LocalStorageInterface;
}

export const useSidebarScreenViewModel = ({
    organization,
    organizations,
    user,
    dashboardUser,
    isSystemAdmin,
    onOrganizationSelected,
    onLockedOptionSelected,
    localStorage = SharedApplication.shared().localStorageController,
}: UseSidebarScreenViewModelParams) => {
    const navigate = useNavigate();
    const location = useLocation();

    const [closedSections, setClosedSections] = useState<SidebarSectionKey[]>(
        () => parseClosedSections(localStorage.getItem(CLOSED_SECTIONS_STORAGE_KEY))
    );

    useEffect(() => {
        localStorage.setItem(CLOSED_SECTIONS_STORAGE_KEY, serializeClosedSections(closedSections));
    }, [closedSections, localStorage]);

    const toggleSection = useCallback((key: Exclude<SidebarSectionKey, 'bottom'>) => {
        setClosedSections((current) => (
            current.includes(key)
                ? current.filter((sectionKey) => sectionKey !== key)
                : [...current, key]
        ));
    }, []);

    const options = useMemo(() => {
        if (!organization) {
            return [];
        }
        const mergedFlags = { ...organization.flags, ...organization.newFlags };
        const builtOptions = buildSidebarOptions({
            entries: sidebarEntries,
            flags: mergedFlags,
            pathname: location.pathname,
        });

        const canShowPilotOnboarding = canAccessPilotOnboarding({
            env: process.env.REACT_APP_FRONT_ENV,
            organizationID: organization.id,
            isSystemAdmin,
        });
        if (canShowPilotOnboarding) {
            builtOptions.push({
                key: PILOT_ONBOARDING_KEY,
                label: PILOT_ONBOARDING_KEY,
                icon: "rocket",
                section: "gestion",
                path: PILOT_ONBOARDING_PATH,
                isLocked: false,
                isActive: location.pathname.startsWith(PILOT_ONBOARDING_PATH),
            });
        }

        return builtOptions;
    }, [organization, location.pathname, isSystemAdmin]);

    const sections: SidebarSection[] = useMemo(() => {
        const orderedSections: Exclude<SidebarSectionKey, 'bottom'>[] = ['principal', 'comunicacion', 'gestion'];
        return orderedSections
            .map((section) => {
                const sectionOptions = options.filter((option) => option.section === section);
                return {
                    key: section,
                    title: sidebarSectionTitles[section],
                    options: sectionOptions,
                    isOpen: !closedSections.includes(section),
                    hiddenCount: sectionOptions.length,
                };
            })
            .filter((section) => section.options.length > 0);
    }, [options, closedSections]);

    const bottomOptions: SidebarOption[] = useMemo(
        () => options.filter((option) => option.section === 'bottom'),
        [options]
    );

    const comboOptions: ComboBoxOption[] = useMemo(
        () => (organizations ?? []).map((org) => ({ id: org.id, title: org.name.trim() })),
        [organizations]
    );

    const comboValue: ComboBoxOption | undefined = useMemo(
        () => comboOptions.find((option) => option.id === organization?.id),
        [comboOptions, organization?.id]
    );

    const selectComboOption = useCallback((option: ComboBoxOption) => {
        const selectedOrganization = (organizations ?? []).find((org) => org.id === option.id);
        if (selectedOrganization && selectedOrganization.id !== organization?.id) {
            onOrganizationSelected(selectedOrganization);
        }
    }, [organizations, organization?.id, onOrganizationSelected]);

    const selectOption = useCallback((option: SidebarOption) => {
        if (option.isLocked) {
            onLockedOptionSelected();
            return;
        }
        if (option.externalLink) {
            const newWindow = window.open(option.externalLink, '_blank');
            newWindow?.focus();
            return;
        }
        if (option.path && option.path !== location.pathname) {
            navigate(option.path);
        }
    }, [location.pathname, navigate, onLockedOptionSelected]);

    const openProfile = useCallback(() => {
        if (location.pathname !== "/profile") {
            navigate("/profile");
        }
    }, [location.pathname, navigate]);

    const userCard: SidebarUserCard = useMemo(() => {
        const name = dashboardUser?.name ?? user?.displayName ?? user?.email ?? "";
        const email = dashboardUser?.email ?? user?.email ?? undefined;
        return {
            name,
            subtitle: email !== name ? email : undefined,
            imageURL: dashboardUser?.image ?? user?.photoURL ?? undefined,
        };
    }, [dashboardUser, user]);

    const isSidebarDisabled = !organization;

    return {
        sections,
        toggleSection,
        bottomOptions,
        comboOptions,
        comboValue,
        selectComboOption,
        selectOption,
        openProfile,
        userCard,
        isSidebarDisabled,
    };
};
